import React from 'react';
import {
  Grid,
  MenuItem,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import FormSectionCard from './FormSectionCard';
import { FormState } from '../../../hooks/useFormState.types';
import { EnergyConsultationFormModel } from '../types';
import TextField from '../../text-field';
import NumericTextField from '../../numeric-text-field';
import { AccountDetail, Maybe } from '../../../__generated__/pge-types';
import { useTranslation } from '../../../hooks/useTranslation';
import mainColors from '../../../themes/main-colors';
import FullServiceAddress from './FullServiceAddress';
import AccountTextField from '../../account-text-field';
import useAccountCustomer from '../../../hooks/useAccountCustomer';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    field: {
      '& .MuiInputBase-root': {
        backgroundColor: mainColors.white,
      },
    },
    readOnlyField: {
      '& .MuiInputBase-root': {
        backgroundColor: mainColors.inputFieldBackground,
      },
    },
  }),
);

interface Props {
  form: FormState<EnergyConsultationFormModel>;
  accounts?: Maybe<AccountDetail[]> | undefined;
  isPublicForm?: boolean;
}

const BusinessInformationSection: React.FC<Props> = ({
  form,
  accounts,
  isPublicForm = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { customer } = useAccountCustomer();

  return (
    <FormSectionCard title={t('BUSINESS_INFORMATION')}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {!isPublicForm && accounts && (
            <TextField
              select
              fullWidth
              variant="outlined"
              label={t('SELECT_ACCOUNT')}
              className={classes.field}
              data-testid="select-account"
              {...form.props('accountNumber')}
              onChange={(event: React.ChangeEvent<{ value: string }>) => {
                const accountNumber = event.target.value;

                void form.setValue('accountNumber', accountNumber);

                const selectedAccount = accounts?.find(
                  acc => acc.accountNumber === accountNumber,
                );

                if (selectedAccount) {
                  const premise = selectedAccount.premiseInfo?.[0];
                  const premiseAddress = premise
                    ? [premise.addressLine1, premise.city, premise.state]
                        .filter(Boolean)
                        .join(', ')
                    : '';
                  void form.setValue(
                    'businessName',
                    selectedAccount.mainCustomerName,
                  );
                  void form.setValue('serviceAddress', premiseAddress);
                  void form.setValue(
                    'postalCode',
                    selectedAccount.premiseInfo?.[0]?.postal,
                  );

                  // Show requestor name fields when the business name and logged in customer name is same
                  // Problem we are trying to solve is when the logged in account is associated to a business name.
                  // Account holder name is displayed as business name and we don’t necessarily know who is requesting the consultation.
                  if (
                    selectedAccount?.mainCustomerName === customer?.personName
                  ) {
                    void form.setValue('isRequestorNameFieldsVisible', true);
                  } else {
                    void form.setValue('isRequestorNameFieldsVisible', false);
                  }
                }
              }}
            >
              {accounts?.map(acc => (
                <MenuItem key={acc.accountNumber} value={acc.accountNumber}>
                  {acc.accountNumber} - {acc.mainCustomerName}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isPublicForm && (
            <AccountTextField
              data-testid="select-account"
              name="accountNumber"
              label={t('ACCOUNT_NUMBER')}
              autoComplete={t('ACCOUNT_NUMBER')}
              {...form.props('accountNumber')}
            />
          )}
        </Grid>

        {!isPublicForm && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label={t('SERVICE_ADDRESS')}
              InputProps={{
                readOnly: true,
              }}
              disabled
              data-testid="service-address"
              className={classes.readOnlyField}
              {...form.props('serviceAddress')}
              InputLabelProps={{
                shrink: !!form.values.serviceAddress,
              }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="outlined"
            label={t('BUSINESS_NAME')}
            {...form.props('businessName')}
            data-testid="business-name"
            value={form.values.businessName}
            InputLabelProps={{
              shrink: !!form.values.businessName,
            }}
          />
        </Grid>

        {isPublicForm && <FullServiceAddress form={form} />}

        <Grid item xs={12}>
          <NumericTextField
            fullWidth
            variant="outlined"
            label={t('NUMBER_OF_EMPLOYEES')}
            data-testid="no-of-employees"
            {...form.props('numberOfEmployees')}
          />
        </Grid>
      </Grid>
    </FormSectionCard>
  );
};

export default BusinessInformationSection;
